import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Empty, Skeleton, Tag } from 'antd';
import {
  NotificationOutlined,
  CloseOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { announcementApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const plainText = (content) =>
  new DOMParser().parseFromString(content || '', 'text/html').body
    .textContent || '';
const AnnouncementBanner = ({ open, onClose, onOpen }) => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissedId, setDismissedId] = useState(null);
  const [revision, setRevision] = useState(0);
  const dismissalKey = 'bee.dismissed-notice:' + user?.username;
  useEffect(() => {
    try {
      setDismissedId(localStorage.getItem(dismissalKey));
    } catch {
      setDismissedId(null);
    }
  }, [dismissalKey]);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    announcementApi
      .getUserAnnouncements(user?.username, 5)
      .then((response) => {
        if (response.data?.code !== 200)
          throw new Error(response.data?.message || '公告暂时无法加载');
        if (current) setAnnouncements(response.data.data || []);
      })
      .catch((failure) => {
        if (current) setError(failure.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [user?.username, revision]);
  const urgent = [...announcements]
    .filter((item) => item.priority <= 1)
    .sort((a, b) => a.priority - b.priority)[0];
  const noticeVersion = urgent
    ? urgent.id + ':' + (urgent.updateTime || urgent.createTime || '')
    : null;
  const dismiss = () => {
    setDismissedId(noticeVersion);
    try {
      localStorage.setItem(dismissalKey, noticeVersion);
    } catch {
      /* The notice can still be dismissed for this page visit. */
    }
  };
  return (
    <>
      {urgent && dismissedId !== noticeVersion && (
        <aside className="urgent-notice" aria-label="重要服务通知">
          <NotificationOutlined aria-hidden="true" />
          <span className="notice-label">服务通知</span>
          <span className="notice-title">
            {plainText(urgent.title).replace(
              /^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u,
              '',
            )}
          </span>
          <Button type="link" size="small" onClick={onOpen}>
            查看 <ArrowRightOutlined />
          </Button>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label="收起服务通知"
            onClick={dismiss}
          />
        </aside>
      )}
      <Drawer
        title="服务通知"
        open={open}
        onClose={onClose}
        width="min(480px, 100vw)"
      >
        <p className="muted">
          显示最近 5 条公告。重要通知关闭后仍可在这里查看。
        </p>
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            action={
              <Button
                size="small"
                onClick={() => setRevision((value) => value + 1)}
              >
                重试
              </Button>
            }
          />
        )}
        {loading ? (
          <Skeleton active={false} />
        ) : !error && !announcements.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无公告" />
        ) : (
          <ul className="notification-list">
            {announcements.map((item) => (
              <li key={item.id}>
                <h3>
                  {item.title}{' '}
                  {item.priority <= 1 && <Tag color="red">紧急</Tag>}
                </h3>
                <p>{plainText(item.content)}</p>
                <span className="notification-meta">{item.createTime}</span>
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </>
  );
};
export default AnnouncementBanner;
